import {useCallback, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import type {Location} from 'history';

// Manually maintained list of pathnames used by the tabs that should be filtered out. Might be possible to make an automatic solution, but currently was the best I could come up with.
const tabPaths = ['/period', '/month-by-month'];

const removeTabpathsFromPathname = (pathname: string) => {
	return tabPaths.reduce((pathname, tabPath) => {
		return pathname ? pathname.replace(tabPath, '') : '';
	}, pathname);
};

export type UseRouterPromptProps = {
	hasChanges: boolean;
	onSave?: (onSuccess: () => void) => void;
	onSaveAs?: (onSuccess: () => void) => void;
	onDiscard?: () => Promise<boolean>; // optional handler to prevent discarding changes by returning false
	ignoreChildPaths?: boolean;
	/** Drops the `beforeunload` listener while keeping the in-app router block and its modal.
	 *
	 * The browser dialog is fixed text — "Do you want to save your changes?" — and cannot be reworded
	 * per caller. A caller whose changes provably cannot be saved any more (the report builder once its
	 * edit lock is lost) would be offering to save work it has already told the user is unsavable, and
	 * a reload the user was explicitly directed to make would be interrupted by it.
	 *
	 * Deliberately separate from `hasChanges`: clearing that suppresses BOTH halves, and the in-app
	 * modal is still the right warning in exactly that state — the work is unrecoverable, so the one
	 * thing left to protect is the user's chance to transcribe it before navigating away. */
	suppressUnloadPrompt?: boolean;
};
type UseRouterPromptReturn = {
	showPrompt: boolean;
	handleSave: () => void;
	handleSaveAs: () => void;
	handleDiscard: () => void;
	closePrompt: () => void;
};

const handleBeforeUnload = (e?: BeforeUnloadEvent) => {
	if (e) {
		e.preventDefault();
		e.returnValue = 'Do you want to save your changes?'; // cannot use intl here - this has to be a plain js function, besides, most browsers show their own prompts
	}
};

export const useRouterPrompt = ({
	hasChanges,
	onSave,
	onSaveAs,
	onDiscard,
	ignoreChildPaths,
	suppressUnloadPrompt,
}: UseRouterPromptProps): UseRouterPromptReturn => {
	const history = useHistory();
	const {location} = history;

	const [showPrompt, setShowPrompt] = useState(false);
	// The whole blocked location, not just its pathname: the navigation is replayed verbatim once the
	// user confirms, and a caller can carry state (the report builder's intent rides in `state`),
	// query params or a hash that the destination needs to render what was asked for.
	const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
	// `history.block` hands back the function that removes the prompt it just registered; holding it
	// is the only way to take that prompt off again.
	const unblockRef = useRef<(() => void) | null>(null);

	const removeBlocker = useCallback(() => {
		window.removeEventListener('beforeunload', handleBeforeUnload);
		// Registering a no-op blocker instead of calling unblock leaves a prompt installed for the rest
		// of the session, which react-router reports as "A history supports only one prompt at a time"
		// as soon as anything else tries to block.
		unblockRef.current?.();
		unblockRef.current = null;
	}, []);

	useEffect(() => {
		if (hasChanges) {
			// handles when page is unloaded (refresh, close, click favourite, etc)
			// it uses the browser default prompt
			if (!suppressUnloadPrompt) {
				window.addEventListener('beforeunload', handleBeforeUnload);
			}
			// internal routing allows a custom prompt to be shown
			unblockRef.current = history.block(prompt => {
				const currentPathnameWithoutTabSuffix = removeTabpathsFromPathname(location?.pathname ?? ''); // Seems like location can be undefined in the tests since there is no parent <Router />. Not sure how to circumvent.
				const skip = ignoreChildPaths && prompt.pathname.includes(currentPathnameWithoutTabSuffix);
				if (!skip) {
					setPendingLocation(prompt);
					setShowPrompt(true);
					return 'true';
				}
			});
		} else {
			removeBlocker();
		}

		return () => {
			removeBlocker();
		};
		// `suppressUnloadPrompt` is a dependency so flipping it mid-session actually takes the listener
		// off: the cleanup below runs `removeBlocker`, and the re-run then re-registers the router block
		// without it.
	}, [history, hasChanges, suppressUnloadPrompt]);

	/** Replays the navigation the blocker caught, exactly as it was requested. */
	const resumeNavigation = useCallback(() => {
		removeBlocker();
		if (pendingLocation) {
			history.push(pendingLocation);
		}
	}, [history, pendingLocation, removeBlocker]);

	const handleSave = useCallback(async () => {
		if (onSave) {
			onSave(resumeNavigation);
		}
	}, [onSave, resumeNavigation]);

	const handleSaveAs = useCallback(async () => {
		if (onSaveAs) {
			onSaveAs(resumeNavigation);
		}
	}, [onSaveAs, resumeNavigation]);

	const handleDiscard = useCallback(async () => {
		const canRoute = onDiscard ? await Promise.resolve(onDiscard()) : true;
		if (canRoute) {
			resumeNavigation();
			setShowPrompt(false);
		}
	}, [onDiscard, resumeNavigation]);

	const closePrompt = useCallback(() => {
		setShowPrompt(false);
	}, []);

	return {showPrompt, handleSave, handleSaveAs, handleDiscard, closePrompt};
};
